import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef
} from "@angular/core";
import { tap } from "rxjs/operators";
import { InsurerProgressItem } from "src/app/share/constants/quotation-progress";
import { QuotationProgressService } from "../quotation-progress.service";

const CHIP_MOVE_MS = 300;

// Desktop progress card, shown above the car details card
@Component({
  selector: "app-quotation-progress-card",
  templateUrl: "./quotation-progress-card.component.html",
  styleUrls: ["./quotation-progress-card.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationProgressCardComponent implements AfterViewChecked {
  // Chip positions captured just before each re-render, so chips that change
  // place (e.g. a quote turns Ready and moves left) can slide there
  private chipPositions: Map<string, DOMRect> | null = null;

  view$ = this.progress.view$.pipe(tap(() => this.captureChipPositions()));

  constructor(
    private progress: QuotationProgressService,
    private host: ElementRef<HTMLElement>
  ) {}

  get showMascot(): boolean {
    return this.progress.showMascot;
  }

  ngAfterViewChecked(): void {
    if (!this.chipPositions) return;
    const before = this.chipPositions;
    this.chipPositions = null;

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return;

    this.chips().forEach((chip) => {
      const previous = before.get(chip.dataset.chipId);
      if (!previous) return;
      const current = chip.getBoundingClientRect();
      const dx = previous.left - current.left;
      const dy = previous.top - current.top;
      if (!dx && !dy) return;
      chip.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
        { duration: CHIP_MOVE_MS, easing: "ease-in-out" }
      );
    });
  }

  scrollTo(id: string): void {
    this.progress.scrollToInsurer(id);
  }

  trackById(_: number, item: InsurerProgressItem): string {
    return item.id;
  }

  private captureChipPositions(): void {
    const chips = this.chips();
    if (!chips.length) return;
    this.chipPositions = new Map(
      chips.map((chip) => [chip.dataset.chipId, chip.getBoundingClientRect()])
    );
  }

  private chips(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>("[data-chip-id]")
    );
  }
}
